/**
 * WF-Other - Composable "Others" input for wf-options
 *
 * Placed INSIDE <wf-options> as a composable child.
 * Contains a text input and a slot for inline buttons.
 * Dispatches 'wf-other-change' event for wf-options to handle.
 *
 * @example
 * <wf-options name="industry" other-name="industry_other">
 *   <wf-option value="tech">Technology</wf-option>
 *   <wf-option value="finance">Finance</wf-option>
 *
 *   <wf-other label="Others, please specify:">
 *     <wf-next-btn inline label="Next →"></wf-next-btn>
 *   </wf-other>
 * </wf-options>
 */

import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

// Import wf-badge for shortcut display
import './wf-badge.js';
// Import wf-layout for grid composition
import './wf-layout.js';

// Co-located styles (Shadow DOM)
import { wfOtherStyles } from './wf-other.styles.js';

export interface OtherChangeDetail {
  value: string;
  name?: string;
}

@customElement('wf-other')
export class WfOther extends LitElement {
  static styles = wfOtherStyles;

  // ============================================
  // Properties
  // ============================================

  /**
   * Label text (default: "Others, please specify:")
   */
  @property({ type: String })
  label = 'Others, please specify:';

  /**
   * Optional hint text shown after the label
   */
  @property({ type: String, attribute: 'label-hint' })
  labelHint?: string;

  /**
   * Input placeholder
   */
  @property({ type: String })
  placeholder = '';

  /**
   * Field name for "other" value (optional, wf-options can auto-derive)
   */
  @property({ type: String })
  name?: string;

  /**
   * Value to use for the parent field when this "Other" input is active.
   * Defaults to "Others" if not specified.
   * @example <wf-other parent-value="Other" label="Other:"></wf-other>
   */
  @property({ type: String, attribute: 'parent-value' })
  parentValue = 'Others';

  /**
   * Make input required when visible
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Disabled state
   */
  @property({ type: Boolean })
  disabled = false;

  /**
   * Keyboard shortcut (set by parent wf-options)
   */
  @property({ type: String })
  shortcut = '';

  // ============================================
  // Internal State
  // ============================================

  @state()
  private _value = '';

  // ============================================
  // Public API
  // ============================================

  /**
   * Get current value
   */
  get value(): string {
    return this._value;
  }

  /**
   * Set value
   */
  set value(val: string) {
    this._value = val;
  }

  /**
   * Clear the input value
   */
  clear(): void {
    this._value = '';
    this._notifyChange();
  }

  /**
   * Focus the input
   */
  focusInput(): void {
    const input = this.shadowRoot?.querySelector<HTMLInputElement>('.wf-other-input');
    input?.focus();
  }

  // ============================================
  // Event Handlers
  // ============================================

  private _handleInput = (e: Event): void => {
    const input = e.target as HTMLInputElement;
    this._value = input.value;
    this._notifyChange();
  };

  private _notifyChange(): void {
    this.dispatchEvent(
      new CustomEvent<OtherChangeDetail>('wf-other-change', {
        detail: {
          value: this._value,
          name: this.name,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  // ============================================
  // Render
  // ============================================

  override render() {
    const hasValue = this._value.trim().length > 0;

    return html`
      <div class="wf-other-container" data-testid="wf-other">
        <label class="wf-other-label">
          ${this.label}
          ${this.labelHint ? html`<span>${this.labelHint}</span>` : nothing}
        </label>
        <div class="wf-other-input-wrapper ${hasValue ? 'has-value' : ''}">
          ${this.shortcut ? html`<wf-badge shortcut="${this.shortcut}"></wf-badge>` : nothing}
          <input
            type="text"
            class="wf-other-input"
            placeholder="${this.placeholder}"
            .value="${this._value}"
            ?disabled="${this.disabled}"
            ?required="${this.required}"
            @input="${this._handleInput}"
            data-testid="wf-other-input"
          />
          <!-- Slot for action buttons (like wf-next-btn) inside input -->
          <div class="wf-other-actions">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-other': WfOther;
  }
}
